'use client';

import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { useBreakpoint } from '@magicschool/spellbook/hooks/useBreakpoint';
import { useLatestRef } from '@magicschool/spellbook/hooks/useLatestRef';
import { cn } from '@magicschool/spellbook/lib/utils';
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { findScrollParent, isZoneFullyVisible, zoneTopInScrollParent } from './scrollUtils';

/** Reason the dismiss was requested. Passed through `onOpenChange` so hosts can branch on source (e.g. analytics, UI state). */
export type BloomDismissReason = 'escape' | 'close-button' | 'backdrop' | 'focus-outside';

export interface BloomProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'role'> {
  /** Controlled open state. */
  open: boolean;
  /**
   * Fires for every dismissal path (Escape, the built-in close button, backdrop click, or
   * focus moving outside the zone). The `reason` argument lets hosts branch on the dismissal
   * source (e.g. analytics, UI state). Pass `returnFocusRef` to restore focus automatically.
   */
  onOpenChange: (open: boolean, reason?: BloomDismissReason) => void;
  /**
   * Accessible name for the surface. Applied as `aria-label` on the group container.
   * To point the surface at a richer host-page heading instead, pass `aria-labelledby`
   * directly on `<Bloom>` — it takes precedence over `aria-label` in AT.
   */
  title: string;
  /** Accessible name for the built-in close button. @default `Close ${title}` */
  closeLabel?: string;
  /** Hide the built-in close button. */
  hideCloseButton?: boolean;
  /** Extra classes merged onto the default close-button positioning. */
  closeButtonClassName?: string;
  /** Size of the built-in close button. Defaults to `'md'`. Pass `'sm'` on compact surfaces. */
  closeButtonSize?: 'sm' | 'md' | 'lg';
  /**
   * Portal target for the content surface (X button + children). Use when the host needs
   * to keep the surface inside a specific layout slot (e.g. a height-locked container
   * that prevents page-level reflow on open). Falls back to `document.body` when omitted.
   */
  portalContainerRef?: RefObject<HTMLElement | null>;
  /**
   * The focus zone — the region of the page that "belongs" to this Bloom surface.
   * When provided, Bloom automatically closes with `reason='focus-outside'` whenever
   * focus moves to an element that is neither inside the zone nor inside Bloom's own
   * content (the close button). Focus is NOT restored on a `focus-outside` dismissal
   * since it has already landed somewhere intentional.
   *
   * The zone ref should point at the container wrapping the primary interactive surface
   * and any focus-mode-only content (prompt starters, suggestions, etc.).
   *
   * Bloom automatically elevates the zone (`position: relative; z-index: --z-focus-content`)
   * while open, so the consumer's content clears the backdrop without any manual z-index wiring.
   *
   * When provided, focusing the built-in close button does not trigger a `focus-outside` dismissal.
   *
   * ```tsx
   * <div ref={zoneRef}>
   *   <ChatInput onFocusChange={(f) => { if (f) setOpen(true); }}>...</ChatInput>
   *   {open && <PromptStarters />}
   * </div>
   * <Bloom open={open} onOpenChange={setOpen} zoneRef={zoneRef} title="..." />
   * ```
   */
  zoneRef?: RefObject<Element | null>;
  /**
   * Ref populated with Bloom's portaled content element (close button + children).
   * Not needed when using `zoneRef` — Bloom wires the focus-outside exclusion automatically.
   */
  contentRef?: RefObject<HTMLElement | null>;
  /**
   * Element to focus after Escape or close-button dismissals. Skipped on `focus-outside`
   * and backdrop dismissals — focus has already landed somewhere intentional.
   *
   * If the target element is also the Bloom open trigger (e.g. a textarea whose focus
   * fires `onFocusChange(true)`), the resulting focus event will reopen the surface.
   * The host is responsible for suppressing that reopen when needed.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * A chromeless focus-mode surface that lifts one element forward and reveals additional
 * content alongside it while the rest of the page softens behind a backdrop.
 *
 * The name evokes a flower: the focused element lifts as petals unfurl — a single
 * primitive that handles both the focus pull and the progressive disclosure that
 * typically accompanies it (prompt-starter cards beneath a chat input, recent results
 * beneath a search field, next-step suggestions beneath a coachmark card, etc).
 *
 * ### When to use
 * - Hero focus modes where a chat input or composer lifts forward and reveals starter
 *   prompts or suggestions (homepage Raina is the canonical case).
 * - Spotlight-style search where a query field opens and reveals recent / suggested
 *   results below it.
 * - Onboarding coachmarks that pin a primary card and reveal contextual next steps.
 * - Celebration / milestone moments that lift a primary card and reveal share / next
 *   actions.
 *
 * ### When NOT to use
 * - Confirmation dialogs, forms, or any content that benefits from card chrome — use
 *   `Modal` instead.
 * - Inline popovers that anchor to a trigger without a backdrop — use `Popover` or `HoverCard`.
 * - Permanent side-panel content — use a layout shell instead.
 *
 * ### Composition
 *
 * The recommended pattern renders the interactive surface (ChatInput, search field, etc.)
 * **outside** Bloom in the normal DOM flow. Bloom sits alongside it and provides the
 * backdrop. Focus-mode-only content (prompt starters, suggestions) is conditionally
 * rendered after the input so Tab reaches it naturally — no focus trap needed.
 *
 * `zoneRef` should wrap both the primary interactive element and any focus-mode-only
 * content. Bloom elevates the zone above the backdrop and auto-closes with
 * `reason='focus-outside'` when Tab leaves it.
 *
 * ```tsx
 * const zoneRef = useRef<HTMLDivElement>(null);
 * const [open, setOpen] = useState(false);
 *
 * // zoneRef wraps both the input and any focus-mode content rendered alongside it.
 * <div ref={zoneRef}>
 *   <ChatInput onFocusChange={(f) => { if (f) setOpen(true); }}>
 *     <ChatInput.Textarea />
 *     <ChatInput.ToolbarEnd><IconButton name="arrow-up" label="Send" /></ChatInput.ToolbarEnd>
 *   </ChatInput>
 *
 *   {/* Focus-mode-only content — Tab-reachable when mounted, gone when closed. *\/}
 *   {open && <PromptStarters onSelect={handleSelect} />}
 * </div>
 *
 * // Bloom provides the backdrop and closes when focus leaves zoneRef.
 * <Bloom open={open} onOpenChange={setOpen} zoneRef={zoneRef} title="Chat with Raina" />
 * ```
 *
 * ### Accessibility
 * - Bloom renders a `role="region"` named via `aria-label` (from the `title` prop). `role="region"`
 *   is a landmark so AT users can navigate to it with standard landmark shortcuts. Tab flows freely
 *   through the page in natural DOM order — focus is never trapped. Do NOT add `aria-expanded` on
 *   the zone's primary interactive element (e.g. a textarea); `aria-expanded` is not a valid
 *   attribute on `role="textbox"` and will trigger axe violations.
 * - Escape and close-button clicks dismiss Bloom. Pass `returnFocusRef` to restore focus
 *   automatically after dismiss. If the target is also the open trigger, focusing it will
 *   reopen the surface — the host must suppress that if unwanted. Focus is never restored
 *   on `focus-outside` dismissals, since focus has already landed somewhere intentional.
 * - The backdrop divs carry `aria-hidden="true"` but underlying page content is NOT hidden from
 *   AT — zone content (which lives outside the group element) remains fully accessible to the
 *   virtual cursor.
 * - The close button portals to `document.body` so `fixed` positioning resolves to the real
 *   viewport. Focusing it never triggers `focus-outside`.
 */
export const Bloom = ({
  open,
  onOpenChange,
  title,
  closeLabel,
  hideCloseButton = false,
  closeButtonClassName,
  closeButtonSize,
  portalContainerRef,
  zoneRef,
  contentRef: contentRefProp,
  returnFocusRef,
  className,
  children,
  ...contentProps
}: BloomProps) => {
  const effectiveCloseLabel = closeLabel ?? `Close ${title}`;
  const smUp = useBreakpoint('sm');
  const effectiveCloseButtonSize = closeButtonSize ?? (smUp ? 'md' : 'sm');

  // Read the portal container after the DOM commit so refs are populated.
  // Synchronous reads at render time are always null on the first open when the
  // host mounts the container and flips `open` in the same render cycle.
  const [contentPortalContainer, setContentPortalContainer] = useState<HTMLElement | undefined>(undefined);
  useLayoutEffect(() => {
    setContentPortalContainer(portalContainerRef?.current ?? undefined);
  }, [portalContainerRef]);

  // When the zone lives inside an `isolation: isolate` stacking context (e.g.
  // SpellbookPageContainer's <main>), a body-portaled backdrop at z-focus-overlay (1100)
  // would sit above that context in the root stacking order, so z-focus-content (1101) on
  // the zone would never clear it. Strategy:
  //
  //   1. Render an inline (non-portaled) backdrop inside the isolate ancestor so the
  //      backdrop (z-focus-overlay) and zone (z-focus-content) compete within the same
  //      stacking context — 1101 > 1100 works correctly for the zone-vs-backdrop ordering.
  //
  //   2. ALSO portal a second backdrop to document.body at z-focus-overlay (1100). This
  //      covers areas outside <main> (the AppBar, top-bar chrome) that the inline backdrop
  //      misses because <main> — and @container/main inside it — use `container-type:
  //      inline-size` which implies `contain: layout`, making @container/main the fixed-
  //      positioning containing block. The inline backdrop's `fixed inset-0` is therefore
  //      relative to @container/main (below the AppBar), not the viewport. The portaled
  //      backdrop escapes that containment and covers the full viewport.
  //
  //   3. Elevate the isolate ancestor to z-focus-content (1101) — one level above the
  //      portaled backdrop (1100) — so <main>'s content stays in front of it, keeping the
  //      zone and the non-zone modules correctly ordered. The portaled Bloom content
  //      at z-focus-content (1101) in root also clears the ancestor.
  const isolateAncestorRef = useRef<HTMLElement | null>(null);
  const [inlineBackdrop, setInlineBackdrop] = useState(false);
  useLayoutEffect(() => {
    if (!zoneRef?.current) {
      isolateAncestorRef.current = null;
      setInlineBackdrop(false);
      return;
    }
    let el: HTMLElement | null = (zoneRef.current as HTMLElement).parentElement;
    while (el && el !== document.body) {
      if (getComputedStyle(el).isolation === 'isolate') {
        isolateAncestorRef.current = el;
        setInlineBackdrop(true);
        return;
      }
      el = el.parentElement;
    }
    isolateAncestorRef.current = null;
    setInlineBackdrop(false);
  }, [zoneRef]);

  useLayoutEffect(() => {
    const ancestor = isolateAncestorRef.current;
    if (!ancestor) return;
    if (open) {
      ancestor.style.position = 'relative';
      ancestor.style.zIndex = 'var(--z-index-focus-content)';
      return () => {
        ancestor.style.position = '';
        ancestor.style.zIndex = '';
      };
    }
  }, [open]);

  const internalContentRef = useRef<HTMLDivElement | null>(null);
  const lastDismissReasonRef = useRef<BloomDismissReason | undefined>(undefined);
  const onOpenChangeRef = useLatestRef(onOpenChange);

  useLayoutEffect(() => {
    const el = zoneRef?.current as HTMLElement | null;
    if (!el) return;
    if (open) {
      el.style.position = 'relative';
      el.style.zIndex = 'var(--z-index-focus-content)';
      // Reserves room below the zone so a consumer's box-shadow (e.g. shadow="elevated") has
      // space to render instead of landing pixel-flush with the viewport edge — box-shadow
      // doesn't contribute to offsetHeight, so clampScroll's clamp below needs this reflected
      // in real layout to account for it.
      el.classList.add('pb-4');
      return () => {
        el.style.position = '';
        el.style.zIndex = '';
        el.classList.remove('pb-4');
      };
    }
    el.style.position = '';
    el.style.zIndex = '';
    el.classList.remove('pb-4');
  }, [open, zoneRef]);

  useEffect(() => {
    if (!open || !zoneRef?.current) return;
    const zone = zoneRef.current as HTMLElement;
    const scrollParent = findScrollParent(zone);

    // Restored on close below, so the user returns to their pre-open position rather than
    // wherever clampScroll's lock happened to leave it.
    const scrollTopBeforeOpen = scrollParent.scrollTop;
    // Tracks intent directly instead of inferring it from zone/viewport geometry: whenever a
    // scroll event reports a scrollTop that clampScroll didn't itself just set, something else
    // (the user, autofill, anchor navigation) moved it — real scroll room the user actually
    // used. This is immune to viewport-shrink cases (mobile keyboard, rotation, split-screen)
    // that a geometry-based check can't cleanly distinguish from genuine content growth.
    let lastClampedScrollTop: number | null = null;
    let userScrolledInZone = false;

    // Only scroll if the zone is clipped. `block: 'start'` would pin it to the top of the
    // scrollport even when it was already fully visible — on the homepage that eats the
    // content-area padding and leaves the page stuck shifted-up if restore is skipped.
    if (!isZoneFullyVisible(zone, scrollParent)) {
      zone.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
    }

    // Keep the zone in view without moving a page that already shows it. `offsetTop` is
    // relative to offsetParent, which is often a positioned wrapper rather than the scroller,
    // so the range is computed from bounding rects instead. If the zone fits, the allowed
    // range includes the current scroll; if it's taller, the user can still scroll within it.
    const clampScroll = () => {
      const view = scrollParent.clientHeight;
      const top = zoneTopInScrollParent(zone, scrollParent);
      const bottom = top + zone.offsetHeight;
      const min = Math.min(top, Math.max(0, bottom - view));
      const max = Math.max(top, bottom - view);
      const clamped = Math.max(min, Math.min(scrollParent.scrollTop, max));
      // Stamp the target first so a sync `scroll` event from assigning scrollTop isn't
      // mistaken for a user gesture (that would skip restore on close).
      lastClampedScrollTop = clamped;
      if (scrollParent.scrollTop !== clamped) scrollParent.scrollTop = clamped;
    };
    const handleScroll = () => {
      if (lastClampedScrollTop !== null && scrollParent.scrollTop !== lastClampedScrollTop) {
        userScrolledInZone = true;
      }
      clampScroll();
    };
    clampScroll();
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    // Content growth (e.g. the input wrapping to more lines) doesn't fire a scroll event, so
    // the clamp needs its own trigger to avoid getting stuck at a stale, too-small bound. Also
    // watch scrollParent itself — its clientHeight can shrink independent of the zone (e.g. a
    // mobile on-screen keyboard opening while typing, which resizes this dvh-based layout).
    const resizeObserver = new ResizeObserver(clampScroll);
    resizeObserver.observe(zone);
    resizeObserver.observe(scrollParent);

    return () => {
      scrollParent.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      // Only if the user never actually scrolled within the zone — otherwise this would
      // discard a deliberate scroll on every close.
      if (!userScrolledInZone) {
        scrollParent.scrollTop = scrollTopBeforeOpen;
      }
    };
  }, [open, zoneRef]);

  // Bounding-box check in addition to contains(): the box catches visually in-zone clicks whose
  // target escapes the zone through a portal or stacking context, and contains() catches zone
  // content that renders outside the box — absolutely-positioned focus-mode content overflows it
  // by design. Capture phase runs before Radix's listeners.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      const inContent = internalContentRef.current?.contains(target);
      const inRadixPopper = Boolean((target as HTMLElement | null)?.closest('[data-radix-popper-content-wrapper]'));
      const inZone = Boolean(zoneRef?.current?.contains(target));
      const zoneBounds = zoneRef?.current?.getBoundingClientRect();
      const inZoneBounds =
        zoneBounds != null &&
        e.clientX >= zoneBounds.left &&
        e.clientX <= zoneBounds.right &&
        e.clientY >= zoneBounds.top &&
        e.clientY <= zoneBounds.bottom;

      const isBloomClose = Boolean((target as HTMLElement | null)?.closest('[data-spellbook-name="bloom-close"]'));
      if (!inZone && !inZoneBounds && !inContent && !inRadixPopper && !isBloomClose) {
        lastDismissReasonRef.current = 'backdrop';
        onOpenChangeRef.current(false, 'backdrop');
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    return () => document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
  }, [open, zoneRef, onOpenChangeRef]);

  // document.focusin instead of React onBlur: Bloom can't attach handlers to the consumer's zone div.
  useEffect(() => {
    if (!open || !zoneRef) return;
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      const inZone = zoneRef.current?.contains(target);
      const inContent = internalContentRef.current?.contains(target);
      // Focus landing on an ancestor of the zone (body, html, main[tabIndex=-1], etc.) is
      // a browser focus-catch for a click on a non-focusable area — not intentional navigation
      // away from Bloom. Suppress dismissal; the zone's contents are still "in context".
      const isZoneAncestor = Boolean(target.contains(zoneRef.current ?? null));
      // Radix popper portals (DropdownMenu, Tooltip, Popover, Combobox) render into
      // document.body under [data-radix-popper-content-wrapper]. Suppress dismissal so
      // menus triggered from zone content (e.g. the ChatInput "+" menu) don't close Bloom.
      const inRadixPopper = Boolean((target as HTMLElement | null)?.closest('[data-radix-popper-content-wrapper]'));
      const isBloomClose = Boolean((target as HTMLElement | null)?.closest('[data-spellbook-name="bloom-close"]'));
      if (!inZone && !isZoneAncestor && !inContent && !inRadixPopper && !isBloomClose) {
        lastDismissReasonRef.current = 'focus-outside';
        onOpenChangeRef.current(false, 'focus-outside');
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [open, zoneRef, onOpenChangeRef]);

  // Escape key — replace the Radix Dialog built-in with a manual listener.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Let any topmost Radix layer (DropdownMenu, Dialog, etc.) handle Escape first.
      // If it called preventDefault, they've already consumed the event — don't also close Bloom.
      if (e.defaultPrevented) return;
      e.preventDefault();
      lastDismissReasonRef.current = 'escape';
      onOpenChangeRef.current(false, 'escape');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChangeRef]);

  // Return focus after close — replaces Radix Dialog's onCloseAutoFocus.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!wasOpen || open) return;
    if (returnFocusRef && lastDismissReasonRef.current !== 'focus-outside') {
      requestAnimationFrame(() => returnFocusRef.current?.focus({ preventScroll: true }));
    }
    lastDismissReasonRef.current = undefined;
  }, [open, returnFocusRef]);

  const backdropCls = cn(
    'fixed inset-0 z-focus-overlay backdrop-frosted',
    'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-300',
  );
  const closeButtonEl = (
    <IconButton
      name="x"
      variant="muted"
      color="neutral"
      size={effectiveCloseButtonSize}
      label={effectiveCloseLabel}
      data-spellbook-name="bloom-close"
      className={cn('fixed top-2 end-2 sm:top-4 sm:end-4 z-focus-close', closeButtonClassName)}
      onClick={() => {
        lastDismissReasonRef.current = 'close-button';
        onOpenChange(false, 'close-button');
      }}
    />
  );

  return (
    <>
      {open &&
        (inlineBackdrop ? (
          <>
            {/* Outer portaled backdrop: escapes @container/main's `contain: layout`
              containing block so `fixed inset-0` reaches the real viewport, covering
              app chrome (AppBar, top-bar) that the inline backdrop can't reach. */}
            {createPortal(<div data-spellbook-name="bloom-backdrop" aria-hidden="true" className={backdropCls} />, document.body)}
            {/* Inline backdrop: lives inside the isolate stacking context so the zone
              can float above it, covering non-zone content within <main>. */}
            <div data-spellbook-name="bloom-backdrop" aria-hidden="true" className={backdropCls} />
          </>
        ) : (
          createPortal(<div data-spellbook-name="bloom-backdrop" aria-hidden="true" className={backdropCls} />, document.body)
        ))}
      {open &&
        createPortal(
          <div
            ref={(el) => {
              internalContentRef.current = el;
              if (contentRefProp) contentRefProp.current = el;
            }}
            role="region"
            aria-label={title}
            data-spellbook-name="bloom"
            className={cn('outline-none w-full relative z-focus-content', className)}
            {...contentProps}
          >
            {!hideCloseButton && closeButtonEl}
            {children}
          </div>,
          contentPortalContainer ?? document.body,
        )}
    </>
  );
};

Bloom.displayName = 'Bloom';
